<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useStore } from "vuex";
import Sparkle from "@/components/Sparkle.vue";

// While a make is being written, the steps the genie works through: what it was given
// (ticked off one after another), any research, then planning until the first words
// arrive and writing as they stream in
const store = useStore();

// What went into this make, as it was when Generate was pressed
const info = store.getters["result/getInfo"];
const prompt = store.state.prompt;

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
const listed = (parts) =>
  parts.length > 1
    ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
    : parts[0];

const given = [
  { doing: "Reading your request", done: "Read your request", detail: prompt },
];
const answers = info.questions?.length || 0;
if (answers) {
  given.push({
    doing: `Taking in your ${plural(answers, "answer")}`,
    done: `Took in your ${plural(answers, "answer")}`,
  });
}
const added = [
  info.documents?.length && plural(info.documents.length, "document"),
  info.images?.length && plural(info.images.length, "photo"),
].filter(Boolean);
if (added.length) {
  given.push({
    doing: `Reading ${listed(added)}`,
    done: `Read ${listed(added)}`,
  });
}
if (info.context?.trim()) {
  given.push({ doing: "Reading your notes", done: "Read your notes" });
}
const style = [
  info.tone,
  info.format,
  info.length && `${info.length} length`,
  info.language,
].filter(Boolean);
if (style.length) {
  given.push({
    doing: "Matching your style",
    done: "Matched your style",
    detail: style.join(" · "),
  });
}

// The things it was given tick off one by one, quickly
const ticked = ref(0);
let timer;
onMounted(() => {
  timer = setInterval(() => {
    if (ticked.value < given.length) ticked.value += 1;
    else clearInterval(timer);
  }, 450);
});
onUnmounted(() => clearInterval(timer));

const text = computed(() => store.state.result.generatedText);
const writing = computed(() => text.value.length > 0);
const words = computed(() => (text.value.match(/\S+/g) || []).length);
const sources = computed(() => store.state.result.sources || []);

const steps = computed(() => {
  const list = given.slice(0, ticked.value + 1).map((step, index) => ({
    ...step,
    finished: index < ticked.value || writing.value,
  }));
  if (ticked.value < given.length && !writing.value) return list;
  let ready = true;
  if (info.research) {
    const found = writing.value || sources.value.length > 0;
    list.push({
      doing: "Researching the web",
      done: sources.value.length
        ? `Researched the web · ${plural(sources.value.length, "source")}`
        : "Researched the web",
      finished: found,
    });
    ready = found;
  }
  if (ready) {
    list.push({
      doing: "Planning what to write",
      done: "Planned what to write",
      finished: writing.value,
    });
  }
  return list;
});

const current = computed(
  () => steps.value.find((step) => !step.finished)?.doing || "Writing"
);
</script>

<template>
  <section class="mb-5" aria-label="What the genie is doing">
    <p class="sr-only" aria-live="polite">{{ current }}</p>
    <div
      v-if="!writing"
      class="relative overflow-hidden rounded-2xl bg-gray-10 p-4 sm:p-5"
      :aria-busy="true"
    >
      <p class="relative mb-3 flex items-center text-sm font-bold text-primary">
        <font-awesome-icon
          :icon="['fas', 'wand-magic-sparkles']"
          class="mr-2 text-accentPrimary"
        />
        The genie is on it
      </p>
      <TransitionGroup tag="ol" name="step" class="relative space-y-2.5">
        <li
          v-for="step in steps"
          :key="step.done"
          class="flex items-start gap-3 text-sm"
        >
          <span
            class="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center"
            aria-hidden="true"
          >
            <template v-if="step.finished">
              <span
                class="tick flex h-5 w-5 items-center justify-center rounded-full bg-accentPrimary text-white"
              >
                <font-awesome-icon
                  :icon="['fas', 'check']"
                  class="text-[10px]"
                />
              </span>
              <!-- A little burst of sparkles as each step is done -->
              <Sparkle
                v-for="n in 3"
                :key="n"
                :size="7"
                class="burst"
                :class="`burst-${n}`"
              />
            </template>
            <template v-else>
              <Sparkle :size="18" twinkle />
              <Sparkle :size="7" twinkle class="satellite" />
            </template>
          </span>
          <span class="min-w-0">
            <span v-if="step.finished" class="text-secondary">{{
              step.done
            }}</span>
            <span v-else class="shimmer font-bold">{{ step.doing }}</span>
            <span
              v-if="step.detail"
              class="block truncate text-xs text-secondary"
              >{{ step.detail }}</span
            >
          </span>
        </li>
      </TransitionGroup>
    </div>
    <p v-else class="flex items-center gap-2.5 text-sm" role="status">
      <span
        class="relative flex h-5 w-5 items-center justify-center"
        aria-hidden="true"
      >
        <Sparkle :size="18" twinkle />
        <Sparkle :size="7" twinkle class="satellite" />
      </span>
      <span class="shimmer font-bold">Writing</span>
      <span class="text-secondary">{{ plural(words, "word") }} so far</span>
    </p>
  </section>
</template>

<style scoped>
.step-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.step-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

/* A smaller sparkle twinkling off-beat beside the one working */
.satellite {
  position: absolute;
  right: -5px;
  top: -4px;
  animation-delay: 0.7s !important;
}

/* Each step done pops its tick in */
.tick {
  animation: pop 0.45s cubic-bezier(0.3, 1.6, 0.5, 1) both;
}
@keyframes pop {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}
.burst {
  position: absolute;
  left: 50%;
  top: 50%;
  opacity: 0;
  animation: burst 0.7s ease-out 0.1s both;
}
.burst-1 {
  --x: -13px;
  --y: -11px;
}
.burst-2 {
  --x: 14px;
  --y: -9px;
}
.burst-3 {
  --x: 3px;
  --y: 14px;
}
@keyframes burst {
  0% {
    transform: translate(-50%, -50%) scale(0.2);
    opacity: 1;
  }
  100% {
    transform: translate(calc(-50% + var(--x)), calc(-50% + var(--y))) scale(1)
      rotate(45deg);
    opacity: 0;
  }
}

/* The step being worked on glints with the brand purple */
.shimmer {
  background: linear-gradient(
    90deg,
    #231f42 0%,
    #231f42 38%,
    #cf5ae2 50%,
    #231f42 62%,
    #231f42 100%
  );
  background-size: 250% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: shimmer 2.4s linear infinite;
}
@keyframes shimmer {
  from {
    background-position: 100% 0;
  }
  to {
    background-position: -150% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-enter-active,
  .tick,
  .shimmer {
    animation: none;
    transition: none;
  }
  .burst {
    display: none;
  }
  .shimmer {
    background: none;
    color: #231f42;
  }
}
</style>
